import { useState, useEffect, useCallback, useRef, lazy, Suspense } from "react";
import { logger } from './utils/logger';
import "@/App.css";
import "@/styles/premium-effects.css";
import "@/styles/premium-modals.css";
import axios from "axios";
// Interceptor global (feb 2026 - envío soporte): blinda parseo de respuestas
// no-JSON (500 de FastAPI, HTML de nginx) para que la app no rompa.
import { installAxiosInterceptors } from "@/utils/axiosInterceptors";
installAxiosInterceptors();
import { Input } from "@/components/ui/input";
// Toast removido por solicitud del usuario
import { MagnifyingGlass, ArrowClockwise, Circle, Bell, CloudArrowUp, Database } from "@phosphor-icons/react";
import Login from "@/components/Login";
import Settings from "@/components/Settings";
import StockTickerHorizontal from "@/components/StockTickerHorizontal";
import CacheMonitor from "@/components/CacheMonitor";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { NewsProvider, useNews } from "@/contexts/NewsContext";
import ThemeSelector from "@/components/ThemeSelector";
// Lazy load DashboardZones for faster initial render
import { LazyDashboardZones } from "@/components/LazyComponents";
import { InitialLoadingSkeleton } from "@/components/ui/skeleton-loaders";
import { PhaseLoadingProgress } from "@/components/ui/detailed-loading-progress";
import { useLoadingQueue } from "@/contexts/LoadingQueueContext";
// Sistema de carga por componentes
import { LoadingProvider, useLoading } from "@/contexts/LoadingContext";
// Sidebar 3D
import Sidebar3D from "@/components/Sidebar3D";
// Menú móvil hamburguesa
import MobileMenu from "@/components/MobileMenu";
// Efectos Premium (solo los necesarios)
import { FloatingParticles, Confetti } from "@/components/PremiumEffects";
// Sistema de Ventanas Flotantes
import WindowManager, { useWindowManager, WINDOWS_CONFIG } from "@/components/WindowManager";
// Landing Page
import LandingPage from "@/components/LandingPage";
// Vista pública de snapshots compartidos (feb 2026)
import SharedView from "@/components/SharedView";
import Taskbar from "@/components/Taskbar";
import LevelCrossWatcher from "@/components/LevelCrossWatcher";
import LayoutPresets from "@/components/LayoutPresets";
// Atajos de teclado
import KeyboardShortcutsPanel, { useKeyboardShortcuts } from "@/components/KeyboardShortcuts";
// Notificaciones
import NotificationManager from "@/components/NotificationManager";
// Historial de alertas
import AlertHistory from "@/components/AlertHistory";
// Panel de gestión de snapshots compartidos (feb 2026)
import MySharesPanel from "@/components/MySharesPanel";
// Comparación rápida con drag & drop
import QuickCompareZone from "@/components/QuickCompareZone";
// Indicador de carga real
import AppReadyIndicator from "@/components/AppReadyIndicator";
// Context global para historial de exportaciones
import { ExportHistoryProvider } from "@/contexts/ExportHistoryContext";
// Sistema de alertas de precio
import { AlertsProvider, useAlerts } from "@/contexts/AlertsContext";
import AlertsPanel from "@/components/AlertsPanel";
// Indicador de actualización con contador
import UpdateIndicator from "@/components/UpdateIndicator";
import TRCloseCountdown from "@/components/TRCloseCountdown";
// Botón de instalación PWA
import InstallPWAButton from "@/components/InstallPWAButton";
// Sistema de autenticación y pagos
import AuthPage from "@/components/AuthPage";
import PricingPage from "@/components/PricingPage";
import PaymentSuccessPage from "@/components/PaymentSuccessPage";
import AdminDashboard from "@/components/AdminDashboard";
import { toast } from "sonner";
import { Toaster } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configurar interceptores una sola vez
let interceptorsConfigured = false;

const setupAxiosInterceptors = () => {
  if (interceptorsConfigured) return;
  
  // Habilitar envío de cookies con cada petición (httpOnly auth)
  axios.defaults.withCredentials = true;
  
  // Interceptor para añadir token de autenticación a todas las peticiones
  axios.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );
  
  // Interceptor para manejar errores de autenticación
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      // Solo disparar authExpired si es un 401 y no es una ruta de autenticación
      if (error.response?.status === 401 && 
          !error.config?.url?.includes('/auth/')) {
        // Token expirado/inválido - la UI manejará el estado
        logger.log('🔐 No autenticado - redirigiendo a login');
        // Disparar evento para que App.js maneje el logout
        window.dispatchEvent(new CustomEvent('authExpired'));
      }
      return Promise.reject(error);
    }
  );
  
  interceptorsConfigured = true;
};

setupAxiosInterceptors();

// Importar configuración desde módulo separado
import { CHART_PERIODS } from './config/chartPeriods';
// Importar hook del contexto de stocks
import { useStock } from './contexts/StockContext';

function AppContent() {
  // Auth state - NUEVO SISTEMA DE USUARIOS
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  // 📱 PWA STATE RESTORATION: leer última página persistida para que al volver
  // de segundo plano (Android suele matar el proceso) el usuario aterrice donde estaba.
  const [currentPage, setCurrentPage] = useState(() => {
    try {
      const saved = localStorage.getItem('kapital_current_page');
      // Solo restaurar páginas internas seguras — nunca 'auth' o 'landing' (los decide verifyAuth)
      if (saved && ['app', 'pricing', 'admin', 'payment-success'].includes(saved)) {
        return saved;
      }
    } catch { /* ignore */ }
    return 'landing';
  }); // 'landing', 'app', 'auth', 'pricing', 'payment-success', 'admin'

  // Persistir currentPage cada vez que cambie
  useEffect(() => {
    try {
      if (['app', 'pricing', 'admin', 'payment-success'].includes(currentPage)) {
        localStorage.setItem('kapital_current_page', currentPage);
      }
    } catch { /* ignore */ }
  }, [currentPage]);
  
  // Estado para la navegación del sidebar - colapsado por defecto en móvil
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    // Barra de herramientas SIEMPRE cerrada por defecto (petición usuario jun 2026):
    // se abre solo manualmente con el botón de toggle, nunca al pasar el ratón.
    return true;
  });
  
  // Estado para mercados enriquecidos (para BigScreenMode)
  const [enrichedMarketsForBigScreen, setEnrichedMarketsForBigScreen] = useState([]);
  
  // Estado para el panel de alertas
  const [showAlertsPanel, setShowAlertsPanel] = useState(false);
  const [alertsPanelMinimized, setAlertsPanelMinimized] = useState(false);
  
  // Estado para botones de guardar/backup
  const [savingToCloud, setSavingToCloud] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  
  // Hook para actualizar markets en el contexto de alertas
  const { updateMarkets: updateAlertsMarkets } = useAlerts();
  
  // Sistema de ventanas flotantes
  const {
    openWindows,
    minimizedWindows,
    focusedWindow,
    openWindow,
    closeWindow,
    minimizeWindow,
    restoreWindow,
    focusWindow,
    closeAllWindows,
    minimizeAllWindows,
    restoreAllWindows,
    getMinimizedWindowsInfo,
    updateWindowState,
    applyLayout,
    restoreLastSession,
    getSavedSessionCount,
  } = useWindowManager();
  
  // Referencia al input de búsqueda
  const searchInputRef = useRef(null);
  
  // Hook para atajos de teclado
  const { showShortcutsPanel, setShowShortcutsPanel } = useKeyboardShortcuts({
    onSearch: () => {
      // Enfocar el input de búsqueda
      searchInputRef.current?.focus();
    },
    onHome: () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    onClose: () => {
      // Cerrar la última ventana abierta
      if (openWindows.length > 0) {
        closeWindow(openWindows[openWindows.length - 1].windowId);
      }
    },
    onOpenWindow: openWindow,
    enabled: isAuthenticated
  });
  
  // Obtener contador de noticias sin leer del contexto
  const { unreadCount: newsCount } = useNews();
  
  // Contador para alertas de precio - conectado al contexto
  const { totalAlerts: alertsCount } = useAlerts();
  
  // Estado para modal de notificaciones
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  
  // Estado para modal de historial de alertas
  const [showAlertHistory, setShowAlertHistory] = useState(false);
  const [showMyShares, setShowMyShares] = useState(false);

  // Listener global para abrir el panel "Mis Compartidos" desde cualquier sitio
  useEffect(() => {
    const h = () => setShowMyShares(true);
    window.addEventListener('openMyShares', h);
    return () => window.removeEventListener('openMyShares', h);
  }, []);
  const [alertHistoryCount, setAlertHistoryCount] = useState(0);
  
  // isVisible siempre retorna true - Mercados Populares y Gráfico siempre visibles
  const isVisible = () => true;
  
  // Usar el contexto de cola de carga
  const {
    currentPhase,
    completedPhases,
    progress: queueProgress,
    isLoading: isQueueLoading,
    startLoadingQueue,
    completeSectionLoading,
    startSectionLoading,
    isSectionEnabled,
    getSectionStatus,
    forceCompleteAll,
    LOADING_PHASES,
    SECTION_CONFIG,
    fastLoadMode,
    toggleFastLoadMode,
  } = useLoadingQueue();
  
  // Usar el contexto de stocks para todo el estado relacionado con stocks
  const {
    ticker, setTicker,
    searchInput, setSearchInput,
    stockInfo, setStockInfo,
    stockHistory, setStockHistory,
    intradayNoData,
    loading, setLoading,
    loadingProgress, setLoadingProgress,
    elapsedSeconds,
    lastUpdate, setLastUpdate,
    autoRefresh, setAutoRefresh,
    chartPeriod, setChartPeriod,
    portfolioTickers, setPortfolioTickers,
    isTabVisible,
    isRefreshing,
    lastVisibilityRefresh,
    fetchStockData,
    loadPortfolioTickers,
  } = useStock();
  
  // Ref local para scroll position (específico de App)
  const savedScrollPosition = useRef(0);

  // El contador de tiempo y visibilidad ya están manejados en StockContext
  
  // Actualizar markets en el contexto de alertas cuando cambien
  useEffect(() => {
    if (enrichedMarketsForBigScreen && enrichedMarketsForBigScreen.length > 0) {
      updateAlertsMarkets(enrichedMarketsForBigScreen);
    }
  }, [enrichedMarketsForBigScreen, updateAlertsMarkets]);
  
  // Detectar visibilidad de la pestaña y actualizar datos
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        logger.log('📱 Pestaña activa - Actualizando datos en segundo plano...');
        // Actualizar silenciosamente cuando vuelve la pestaña
        if (ticker && isAuthenticated) {
          fetchStockData(ticker, chartPeriod.period, chartPeriod.interval, true, chartPeriod.aggregate || 1);
        }
      } else {
        logger.log('📱 Pestaña oculta - Pausando actualizaciones...');
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [ticker, chartPeriod, isAuthenticated, fetchStockData]);

  // Keep-alive ULTRA AGRESIVO: Mantener la conexión activa mientras se usa la app
  // + REFRESH AUTOMÁTICO: Refrescar token antes de que expire
  useEffect(() => {
    if (!isAuthenticated) return;
    
    const KEEP_ALIVE_INTERVAL = 2 * 60 * 1000; // 2 minutos - más frecuente para evitar desconexiones
    const TOKEN_REFRESH_INTERVAL = 60 * 60 * 1000; // Verificar refresh cada hora
    let lastActivity = Date.now();
    let lastTokenRefresh = Date.now();
    let keepAliveFailCount = 0;
    
    const sendKeepAlive = async () => {
      try {
        const timestamp = Date.now();
        // Solo llamar a /health - sin verificación de auth
        await axios.get(`${API}/health?t=${timestamp}`, { withCredentials: true, timeout: 10000 });
        logger.log('💓 Keep-alive OK - ' + new Date().toLocaleTimeString());
        keepAliveFailCount = 0; // Reset contador de fallos
      } catch (error) {
        keepAliveFailCount++;
        logger.log(`💔 Keep-alive falló (intento ${keepAliveFailCount})`);
        
        // Si falla 3 veces seguidas, intentar re-verificar auth
        if (keepAliveFailCount >= 3) {
          try {
            await axios.get(`${API}/auth/verify`, { withCredentials: true, timeout: 10000 });
            logger.log('✅ Verificación de auth OK después de fallos de keep-alive');
            keepAliveFailCount = 0;
          } catch (authError) {
            logger.log('⚠️ Auth verification también falló - posible desconexión de servidor');
          }
        }
      }
    };
    
    // Refrescar token manualmente cada 24 horas de uso activo
    const refreshTokenIfNeeded = async () => {
      const hoursSinceRefresh = (Date.now() - lastTokenRefresh) / (1000 * 60 * 60);
      if (hoursSinceRefresh >= 24) {
        try {
          await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true, timeout: 5000 });
          logger.log('🔄 Token refrescado manualmente - sesión extendida');
          lastTokenRefresh = Date.now();
        } catch (e) {
          logger.log('⚠️ No se pudo refrescar el token');
        }
      }
    };
    
    // Enviar keep-alive cada 30 segundos
    const intervalId = setInterval(sendKeepAlive, KEEP_ALIVE_INTERVAL);
    
    // Verificar refresh de token cada hora
    const tokenRefreshInterval = setInterval(refreshTokenIfNeeded, TOKEN_REFRESH_INTERVAL);
    
    // Enviar uno inmediatamente al montar
    sendKeepAlive();
    
    // Detectar actividad del usuario (mouse, teclado, scroll)
    const handleActivity = () => {
      lastActivity = Date.now();
    };
    
    // También enviar cuando la pestaña se hace visible de nuevo
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        sendKeepAlive();
      }
    };
    
    // Enviar keep-alive con cada clic (ventanas, botones, etc.)
    const handleClick = () => {
      sendKeepAlive();
    };
    
    document.addEventListener('visibilitychange', handleVisibility);
    document.addEventListener('mousemove', handleActivity, { passive: true });
    document.addEventListener('keydown', handleActivity, { passive: true });
    document.addEventListener('click', handleClick, { passive: true });
    document.addEventListener('scroll', handleActivity, { passive: true });
    
    return () => {
      clearInterval(intervalId);
      clearInterval(tokenRefreshInterval);
      document.removeEventListener('visibilitychange', handleVisibility);
      document.removeEventListener('mousemove', handleActivity);
      document.removeEventListener('keydown', handleActivity);
      document.removeEventListener('click', handleClick);
      document.removeEventListener('scroll', handleActivity);
    };
  }, [isAuthenticated]);

  // Escuchar evento del ticker horizontal para abrir gráfico interactivo
  useEffect(() => {
    const handleOpenTickerChart = (event) => {
      const { ticker: selectedTicker, name } = event.detail;
      if (selectedTicker) {
        // Cambiar el ticker principal y abrir el gráfico
        setSearchInput(selectedTicker);
        setTicker(selectedTicker);
        fetchStockData(selectedTicker, chartPeriod.period, chartPeriod.interval, false, chartPeriod.aggregate || 1);
        
        // Scroll al gráfico principal
        const chartElement = document.querySelector('[data-testid="interactive-chart"]')
          || document.querySelector('[data-testid="chart-section"]');
        if (chartElement) {
          chartElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    };
    
    window.addEventListener('openTickerChart', handleOpenTickerChart);
    return () => window.removeEventListener('openTickerChart', handleOpenTickerChart);
  }, [chartPeriod, setSearchInput, setTicker, fetchStockData]);

  // Restaurar scroll después de que los datos se hayan cargado completamente
  useEffect(() => {
    if (loadingProgress === 100 && savedScrollPosition.current > 0) {
      const targetScroll = savedScrollPosition.current;
      
      // Restaurar scroll múltiples veces para asegurar que funcione
      const restoreScroll = () => {
        window.scrollTo(0, targetScroll);
      };
      
      // Restaurar inmediatamente
      restoreScroll();
      
      // Y también después de varios delays para cubrir todos los re-renders
      const t1 = setTimeout(restoreScroll, 50);
      const t2 = setTimeout(restoreScroll, 150);
      const t3 = setTimeout(() => {
        restoreScroll();
        savedScrollPosition.current = 0; // Reset después de restaurar
      }, 300);
      
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
        clearTimeout(t3);
      };
    }
  }, [loadingProgress]);

  // Auto-colapsar el sidebar cuando la app termina de cargar
  useEffect(() => {
    // Cerrar sidebar cuando queueProgress o loadingProgress llega a 100
    const isFullyLoaded = queueProgress >= 100 || loadingProgress >= 100;
    if (isFullyLoaded && !sidebarCollapsed) {
      // Esperar un poco para que el usuario vea que la app está lista
      const timer = setTimeout(() => {
        setSidebarCollapsed(true);
      }, 2000); // 2 segundos después de cargar
      return () => clearTimeout(timer);
    }
  }, [loadingProgress, queueProgress, sidebarCollapsed]);

  // Listener para manejar expiración de autenticación (evento authExpired del interceptor 401)
  useEffect(() => {
    const handleAuthExpired = () => {
      console.log('🔐 Sesión expirada - cerrando sesión...');
      // Limpiar estado de autenticación sin recargar la página
      localStorage.removeItem('auth_token');
      localStorage.removeItem('token');
      localStorage.removeItem('user_data');
      localStorage.removeItem('current_user_id');
      document.cookie = 'access_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      document.cookie = 'auth_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      
      setCurrentUser(null);
      setIsAuthenticated(false);
      setCurrentPage('auth'); // Redirigir a login
    };
    
    window.addEventListener('authExpired', handleAuthExpired);
    return () => window.removeEventListener('authExpired', handleAuthExpired);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchInput.trim()) {
      // Guardar scroll antes de buscar
      savedScrollPosition.current = window.scrollY;
      setTicker(searchInput.trim().toUpperCase());
    }
  };

  const handleTickerSelect = (selectedTicker, options = {}) => {
    // Si viene de una tarjeta (scroll al gráfico), NO guardar/restaurar scroll
    savedScrollPosition.current = options.scrollToChart ? 0 : window.scrollY;
    setSearchInput(selectedTicker);
    setTicker(selectedTicker);
    
    // Cargar datos del nuevo ticker inmediatamente
    fetchStockData(selectedTicker, chartPeriod.period, chartPeriod.interval, false, chartPeriod.aggregate || 1);
  };

  const handleManualRefresh = async () => {
    // Hard refresh - limpiar caché y recargar
    try {
      // Disparar evento de recarga manual y guardar timestamp
      window.dispatchEvent(new Event('manualReload'));
      localStorage.setItem('lastManualReload', Date.now().toString());
      
      // 1. Limpiar caché del Service Worker si existe
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
      }
      
      // 2. NO borrar localStorage - mantener TODOS los datos del usuario
      // Solo recargamos la página sin borrar nada
      
      // 3. Recargar con parámetro único para evitar caché del navegador
      const url = new URL(window.location.href);
      url.searchParams.set('refresh', Date.now());
      window.location.href = url.toString();
    } catch (e) {
      // Fallback: recarga simple
      window.location.reload();
    }
  };

  // Actualizar solo datos sin recargar página
  const handleSoftRefresh = () => {
    // Guardar scroll antes de actualizar
    savedScrollPosition.current = window.scrollY;
    // Resetear contador de próxima actualización
    resetCountdown();
    // Actualizar datos del ticker actual
    fetchStockData(ticker, chartPeriod.period, chartPeriod.interval, false, chartPeriod.aggregate || 1);
    // Disparar evento para que otros componentes actualicen
    window.dispatchEvent(new CustomEvent('refreshData'));
  };

  const handlePeriodChange = async (newPeriod) => {
    // Guardar posición de scroll actual para evitar saltos
    const scrollPosition = window.scrollY;
    
    setStockHistory([]); // Limpiar datos anteriores para evitar errores
    
    // Actualizamos chartPeriod optimísticamente: la barra de herramientas debe
    // reflejar SIEMPRE la elección del usuario aunque yfinance esté rate-limited
    // o devuelva error. Si el fetch tiene éxito y matchea otra entrada válida,
    // StockContext lo re-sincroniza al final.
    setChartPeriod(newPeriod);
    
    // No establecemos el período aquí - lo dejamos que fetchStockData lo haga
    // después de verificar si hay datos (para manejar el fallback)
    await fetchStockData(ticker, newPeriod.period, newPeriod.interval, false, newPeriod.aggregate || 1);
    
    // Solo establecer el período si el fallback no lo cambió
    // (fetchStockData puede haber cambiado a 1M si no hay datos para 1D/5D)
    
    // Restaurar posición de scroll después del re-render
    requestAnimationFrame(() => {
      window.scrollTo(0, scrollPosition);
    });
  };

  // Escuchar actualizaciones del Service Worker
  useEffect(() => {
    const handleSWMessage = (event) => {
      if (event.data?.type === 'SW_UPDATED') {
        console.log(`🔄 Service Worker actualizado a v${event.data.version}`);
        // Mostrar alerta al usuario
        if (window.confirm('¡Nueva versión disponible! ¿Deseas recargar para ver las actualizaciones?')) {
          window.location.reload();
        }
      }
    };
    
    // Escuchar mensajes del Service Worker
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.addEventListener('message', handleSWMessage);
    }
    
    // También registrar nuevo SW si existe y forzar actualización
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistration().then((registration) => {
        if (registration) {
          registration.update().then(() => {
            console.log('🔄 Verificando actualizaciones del Service Worker...');
          });
        }
      });
    }
    
    return () => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleSWMessage);
      }
    };
  }, []);

  // Verificar autenticación al cargar - NUEVO SISTEMA DE USUARIOS
  useEffect(() => {
    const verifyAuth = async () => {
      console.log('🔐 Verificando autenticación con nuevo sistema...');
      
      // Verificar si hay URL de pago exitoso
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('session_id')) {
        setCurrentPage('payment-success');
        setCheckingAuth(false);
        return;
      }

      // Deep-link a página de planes (?page=pricing) — útil para compartir
      const forcePricing = urlParams.get('page') === 'pricing';
      
      // Verificar si hay token guardado
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      
      if (token) {
        // ⚡ FAST-AUTH (PWA restore): si hay user_data cacheado, hidratar la app YA
        // y verificar el token en segundo plano. Esto elimina el "pantallazo de carga"
        // al volver de segundo plano en Android cuando Chrome mata el proceso.
        const cachedUserData = localStorage.getItem('user_data');
        if (cachedUserData) {
          try {
            const userData = JSON.parse(cachedUserData);
            setCurrentUser(userData);
            setIsAuthenticated(true);
            // Respetar la última página persistida si existe y es segura
            const savedPage = localStorage.getItem('kapital_current_page');
            const safeSavedPage = ['app', 'pricing', 'admin'].includes(savedPage) ? savedPage : 'app';
            setCurrentPage(forcePricing ? 'pricing' : safeSavedPage);
            setCheckingAuth(false);
            console.log('⚡ Fast-auth: sesión restaurada desde caché para', userData.email);

            // Verificación en segundo plano (no bloqueante)
            axios.get(`${API}/auth/me`, {
              headers: { 'Authorization': `Bearer ${token}` },
              timeout: 15000
            }).then((response) => {
              localStorage.setItem('user_data', JSON.stringify(response.data));
              setCurrentUser(response.data);
              console.log('✅ Sesión verificada en background:', response.data.email);
            }).catch((error) => {
              // Si el token es inválido (401), limpiar. Errores de red se ignoran.
              if (error.response?.status === 401) {
                console.log('⚠️ Token expirado - cerrando sesión');
                localStorage.removeItem('auth_token');
                localStorage.removeItem('token');
                localStorage.removeItem('user_data');
                localStorage.removeItem('kapital_current_page');
                setIsAuthenticated(false);
                setCurrentPage('landing');
              }
            });
            return;
          } catch { /* parse error - continuar con verificación normal */ }
        }

        try {
          // Verificar token con el servidor - timeout aumentado para servidores lentos
          const response = await axios.get(`${API}/auth/me`, {
            headers: { 'Authorization': `Bearer ${token}` },
            timeout: 15000 // 15 segundos para dar tiempo al servidor
          });
          
          console.log('✅ Usuario autenticado:', response.data.email);
          // Guardar user_data si no existe
          localStorage.setItem('user_data', JSON.stringify(response.data));
          setCurrentUser(response.data);
          setIsAuthenticated(true);
          setCurrentPage(forcePricing ? 'pricing' : 'app');
        } catch (error) {
          // Si es timeout o error de red, usar datos locales como fallback
          const savedUserData = localStorage.getItem('user_data');
          if ((error.code === 'ECONNABORTED' || error.message?.includes('timeout') || error.message?.includes('Network')) && savedUserData) {
            try {
              const userData = JSON.parse(savedUserData);
              console.log('⚠️ Servidor lento, usando datos locales para:', userData.email);
              setCurrentUser(userData);
              setIsAuthenticated(true);
              setCurrentPage('app');
              return; // No limpiar sesión si tenemos datos válidos
            } catch (parseError) {
              // Si no se pueden parsear los datos, continuar con logout
            }
          }
          
          console.log('⚠️ Token inválido, limpiando sesión...', error.message);
          localStorage.removeItem('auth_token');
          localStorage.removeItem('token');
          localStorage.removeItem('user_data');
          setIsAuthenticated(false);
          setCurrentPage('landing');
        }
      } else {
        console.log('🔓 No hay sesión, mostrando landing...');
        setIsAuthenticated(false);
        setCurrentPage('landing');
      }
      
      setCheckingAuth(false);
    };

    verifyAuth();
  }, []);

  // 🔄 RECONEXIÓN AUTOMÁTICA cuando la pestaña vuelve a estar activa
  useEffect(() => {
    let lastActiveTime = Date.now();
    let wasHidden = false;
    
    const wakeUpServer = async () => {
      // Intentar despertar el servidor con múltiples intentos
      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          console.log(`🌅 Intentando despertar servidor (intento ${attempt}/3)...`);
          await axios.get(`${API}/health`, { timeout: 10000 });
          console.log('✅ Servidor despierto');
          return true;
        } catch (error) {
          if (attempt < 3) {
            await new Promise(resolve => setTimeout(resolve, 2000));
          }
        }
      }
      return false;
    };
    
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        // Solo actuar si la pestaña estaba oculta antes
        if (!wasHidden) {
          wasHidden = false;
          return;
        }
        
        const inactiveTime = Date.now() - lastActiveTime;
        const inactiveSeconds = inactiveTime / 1000;
        
        console.log(`📱 Pestaña activa de nuevo (inactiva ${inactiveSeconds.toFixed(0)} segundos)`);
        
        // Si estuvo inactiva más de 30 segundos, verificar servidor
        if (inactiveSeconds > 30) {
          console.log('🔄 Verificando conexión...');
          
          // Despertar el servidor si está dormido
          const serverAwake = await wakeUpServer();
          
          if (serverAwake) {
            // Verificar si el token aún existe en localStorage
            const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
            if (token) {
              console.log('✅ Token presente - sesión asumida válida');
            }
            // No re-autenticar automáticamente - dejar que el interceptor 401 maneje expiración
            
            // Disparar evento para que los componentes refresquen sus datos
            window.dispatchEvent(new CustomEvent('appResumed', { detail: { inactiveSeconds } }));
          } else {
            console.warn('⚠️ Servidor no responde - esperando reconexión...');
            // NO recargar la página - dejar que el usuario decida
          }
        }
        
        wasHidden = false;
        lastActiveTime = Date.now();
      } else {
        // Pestaña se va a segundo plano
        wasHidden = true;
        lastActiveTime = Date.now();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // 📱 PWA STATE PERSISTENCE (Android): guarda scroll + estado JUSTO antes de
  // que el proceso se suspenda/muera. En Android, `visibilitychange -> hidden`
  // y `pagehide` son la única ventana confiable para persistir estado (el
  // navegador puede matar la app sin previo aviso mientras está minimizada).
  useEffect(() => {
    const persistState = () => {
      try {
        localStorage.setItem('kapital_last_scroll', String(window.scrollY || 0));
        localStorage.setItem('kapital_last_hidden_at', String(Date.now()));
      } catch { /* ignore */ }
    };

    const handleHidden = () => {
      if (document.visibilityState === 'hidden') persistState();
    };

    document.addEventListener('visibilitychange', handleHidden);
    window.addEventListener('pagehide', persistState);
    window.addEventListener('beforeunload', persistState);

    return () => {
      document.removeEventListener('visibilitychange', handleHidden);
      window.removeEventListener('pagehide', persistState);
      window.removeEventListener('beforeunload', persistState);
    };
  }, []);

  // Restaurar scroll al arrancar (una sola vez cuando la app entre en 'app')
  useEffect(() => {
    if (currentPage !== 'app' || checkingAuth) return;
    try {
      const savedScroll = parseInt(localStorage.getItem('kapital_last_scroll') || '0', 10);
      const hiddenAt = parseInt(localStorage.getItem('kapital_last_hidden_at') || '0', 10);
      // Solo restaurar si la app se ocultó hace menos de 6 horas (evita restaurar tras días)
      if (savedScroll > 0 && Date.now() - hiddenAt < 6 * 60 * 60 * 1000) {
        // Esperar a que el DOM esté listo para hacer scroll
        const restore = () => window.scrollTo({ top: savedScroll, behavior: 'instant' in window ? 'instant' : 'auto' });
        setTimeout(restore, 300);
        setTimeout(restore, 900); // segundo intento tras carga inicial de widgets
      }
    } catch { /* ignore */ }
    // Solo queremos ejecutar esto cuando pasamos a 'app' la primera vez
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage === 'app', checkingAuth]);

  // 🔄 pageshow: detecta cuando el navegador restaura la app desde BFCache
  // (algunos Android/Chrome lo soportan). En ese caso NO recargar, solo
  // refrescar datos silenciosamente.
  useEffect(() => {
    const handlePageShow = (event) => {
      if (event.persisted) {
        console.log('♻️ Restaurado desde BFCache - refrescando datos silenciosamente');
        window.dispatchEvent(new CustomEvent('appResumed', { detail: { fromBFCache: true } }));
      }
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  // 📱 TOAST "Sincronizando..." al volver de segundo plano.
  // Muestra un indicador breve solo si la app estuvo oculta > 3 segundos
  // (evita ruido en cambios de foco fugaces). Se muestra en cualquier
  // restauración: BFCache, visibilitychange visible, o pageshow.
  useEffect(() => {
    if (!isAuthenticated) return;
    let hiddenAt = 0;
    let toastId = null;

    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now();
      } else if (document.visibilityState === 'visible' && hiddenAt > 0) {
        const inactiveMs = Date.now() - hiddenAt;
        hiddenAt = 0;
        if (inactiveMs > 3000) {
          // Descartar cualquier toast anterior de sincronización
          if (toastId) toast.dismiss(toastId);
          toastId = toast.loading('Sincronizando datos...', {
            duration: 1800,
            position: 'top-center',
            className: 'kapital-sync-toast'
          });
          // Auto-descartar tras 1.8s
          setTimeout(() => {
            if (toastId) {
              toast.dismiss(toastId);
              toastId = null;
            }
          }, 1800);
        }
      }
    };

    const handleAppResumed = () => {
      if (toastId) toast.dismiss(toastId);
      toastId = toast.loading('Sincronizando datos...', {
        duration: 1500,
        position: 'top-center',
        className: 'kapital-sync-toast'
      });
      setTimeout(() => {
        if (toastId) {
          toast.dismiss(toastId);
          toastId = null;
        }
      }, 1500);
    };

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('appResumed', handleAppResumed);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('appResumed', handleAppResumed);
      if (toastId) toast.dismiss(toastId);
    };
  }, [isAuthenticated]);

  // MANUAL TOTAL (oct 2026, petición usuario): se ELIMINA la precarga automática
  // de noticias al iniciar sesión. Antes esto disparaba la construcción costosa
  // del payload (Google News por-ticker) en cada login. El contador del sidebar
  // se actualizará cuando el usuario cargue las noticias manualmente.

  // Iniciar cola de carga y progreso cuando el usuario está autenticado
  useEffect(() => {
    if (isAuthenticated) {
      // Iniciar la cola de carga por fases
      startLoadingQueue();
      // Cargar datos del ticker - NO usar silentUpdate para que transite el estado loading
      // y el useEffect de AdvancedStockChart detecte la carga completa
      fetchStockData(ticker, chartPeriod.period, chartPeriod.interval, false, chartPeriod.aggregate || 1);
      
      // Los componentes MarketsList y AdvancedStockChart reportan su carga directamente
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, startLoadingQueue]);
  
  // NOTA: Fase 2 y 3 ya no usan setTimeout falsos
  // Los componentes de cada sección reportan su carga real usando LoadingContext
  // Las secciones se habilitan automáticamente cuando la fase anterior completa
  // (ver LoadingQueueContext.js)

  // Cargar tickers del portfolio para noticias (desde las inversiones del usuario)
  useEffect(() => {
    const loadPortfolioTickers = async () => {
      if (!isAuthenticated) return;
      try {
        // Ya no se necesita token manual - cookie se envía automáticamente
        const response = await axios.get(`${API}/user/investments`);
        const investments = response.data.investments || {};
        // Extraer solo los tickers que tienen inversiones válidas
        const tickers = Object.keys(investments).filter(ticker => 
          investments[ticker].numShares > 0 && investments[ticker].purchasePrice > 0
        );
        setPortfolioTickers(tickers);
      } catch (error) {
        logger.error('Error loading portfolio tickers:', error);
      }
    };
    loadPortfolioTickers();
    
    // Escuchar cambios en las inversiones
    window.addEventListener('investmentsUpdated', loadPortfolioTickers);
    return () => window.removeEventListener('investmentsUpdated', loadPortfolioTickers);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Función para disparar evento de reset del contador (usada por handleSoftRefresh)
  const resetCountdown = useCallback(() => {
    window.dispatchEvent(new Event('resetUpdateCountdown'));
  }, []);

  // Escuchar evento de auto-refresh desde el contador (cuando llega a 0)
  useEffect(() => {
    if (!isAuthenticated) return;
    
    const handleAutoRefresh = () => {
      if (document.visibilityState === 'visible') {
        console.log('🔄 Auto-actualización disparada por contador (5 min)');
        fetchStockData(ticker, chartPeriod.period, chartPeriod.interval, true);
      }
    };
    
    window.addEventListener('autoRefreshTrigger', handleAutoRefresh);
    return () => window.removeEventListener('autoRefreshTrigger', handleAutoRefresh);
  }, [isAuthenticated, ticker, chartPeriod, fetchStockData]);

  // Refrescar datos cuando la pestaña vuelve a estar visible (después de inactividad)
  // NO resetea el contador - solo actualiza datos silenciosamente
  useEffect(() => {
    if (!isAuthenticated || !isTabVisible) return;
    
    // Refrescar datos al volver a la pestaña (sin resetear contador)
    if (isTabVisible && !isRefreshing.current) {
      isRefreshing.current = true;
      fetchStockData(ticker, chartPeriod.period, chartPeriod.interval, true, chartPeriod.aggregate || 1).finally(() => {
        isRefreshing.current = false;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTabVisible, isAuthenticated, ticker, fetchStockData, chartPeriod]);

  const handleLoginSuccess = (data) => {
    // data puede ser {access_token, user} o el objeto user directamente
    const user = data.user || data;
    // Colapsar todos los widgets del dashboard tras el login para una carga más
    // rápida (el usuario abre los que quiera desde el botón "Widgets").
    try { sessionStorage.setItem('kap_collapse_dashboard_on_login', '1'); } catch (_) { /* noop */ }
    setCurrentUser(user);
    setIsAuthenticated(true);
    setCheckingAuth(false);
    setCurrentPage('app');
    
    // Disparar evento para forzar recarga de datos en todos los componentes
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('userLoggedIn', { detail: { user } }));
    }, 100);

    // Deep-link: si el visitante buscó un activo en la landing, abrir su análisis tras login
    try {
      const pending = sessionStorage.getItem('kap_pending_analysis');
      if (pending) {
        sessionStorage.removeItem('kap_pending_analysis');
        setTimeout(() => { try { openWindow('analyzer', { ticker: pending }); } catch (_) { /* noop */ } }, 900);
      }
    } catch (_) { /* noop */ }
  };

  const handleLogout = async () => {
    // Limpiar token y datos de usuario
    localStorage.removeItem('auth_token');
    localStorage.removeItem('token');
    localStorage.removeItem('user_data');
    localStorage.removeItem('current_user_id');
    // Limpiar datos de mercados del usuario
    localStorage.removeItem('popularMarketsSelection');
    localStorage.removeItem('myInvestments');
    localStorage.removeItem('priceAlerts');
    // Limpiar estado de PWA restauración
    localStorage.removeItem('kapital_current_page');
    localStorage.removeItem('kapital_last_scroll');
    localStorage.removeItem('kapital_last_hidden_at');
    
    // Limpiar cookies de autenticación
    document.cookie = 'access_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    document.cookie = 'auth_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    
    // Llamar al endpoint de logout del backend para limpiar cookies httpOnly
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (e) {
      // Ignorar errores de logout del backend
    }
    
    setCurrentUser(null);
    setIsAuthenticated(false);
    setCurrentPage('landing');
  };

  // ============================================================================
  // Rutas públicas (feb 2026): /share/:id renderiza un snapshot sin auth.
  // Se hace ANTES del check de isAuthenticated para permitir acceso libre.
  // ============================================================================
  if (typeof window !== 'undefined' && /^\/share\/[A-Za-z0-9_-]+/.test(window.location.pathname)) {
    return (
      <ThemeProvider>
        <SharedView />
      </ThemeProvider>
    );
  }

  // Mostrar Landing Page para nuevos visitantes
  if (currentPage === 'landing' && !isAuthenticated) {
    return (
      <ThemeProvider>
        <LandingPage 
          onGetStarted={(ticker) => {
            if (ticker && typeof ticker === 'string') {
              try { sessionStorage.setItem('kap_pending_analysis', ticker.toUpperCase()); } catch (_) { /* noop */ }
            }
            setCurrentPage('auth');
          }}
        />
      </ThemeProvider>
    );
  }

  // Mostrar página de autenticación
  if (currentPage === 'auth' && !isAuthenticated) {
    return (
      <ThemeProvider>
        <AuthPage 
          onSuccess={handleLoginSuccess}
          onBack={() => setCurrentPage('landing')}
        />
      </ThemeProvider>
    );
  }

  // Mostrar página de precios
  if (currentPage === 'pricing') {
    return (
      <ThemeProvider>
        <PricingPage 
          user={currentUser}
          onBack={() => setCurrentPage('app')}
          onLogin={() => setCurrentPage('auth')}
        />
      </ThemeProvider>
    );
  }

  // Mostrar página de pago exitoso
  if (currentPage === 'payment-success') {
    return (
      <ThemeProvider>
        <PaymentSuccessPage 
          onContinue={() => {
            // Limpiar URL y volver al app
            window.history.replaceState({}, document.title, window.location.pathname);
            setCurrentPage('app');
            setIsAuthenticated(true);
            // Recargar datos del usuario
            let userData = {};
            try {
              const userDataStr = localStorage.getItem('user_data');
              if (userDataStr) userData = JSON.parse(userDataStr);
            } catch (e) {
              console.warn('Error parsing user_data:', e.message);
            }
            setCurrentUser(userData);
          }}
        />
      </ThemeProvider>
    );
  }

  // Mostrar panel de administración
  if (currentPage === 'admin') {
    return (
      <ThemeProvider>
        <AdminDashboard 
          user={currentUser}
          onBack={() => setCurrentPage('app')}
        />
      </ThemeProvider>
    );
  }

  // Mostrar pantalla de carga mientras verifica auth - con indicador visual atractivo
  if (checkingAuth) {
    return (
      <ThemeProvider>
        <div className="App">
          <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950">
            <div className="text-center">
              {/* Indicador de batería/carga animado */}
              <div className="relative w-48 h-24 mx-auto mb-6">
                {/* Batería 3D */}
                <div className="relative w-40 h-20 mx-auto rounded-xl bg-zinc-800 border-2 border-gold-500/50 shadow-lg shadow-gold-500/20 overflow-hidden"
                  style={{ transform: 'perspective(400px) rotateX(5deg)' }}>
                  {/* Terminal de la batería */}
                  <div className="absolute -right-3 top-1/2 -translate-y-1/2 w-3 h-8 bg-zinc-800 border-2 border-l-0 border-gold-500/50 rounded-r-md"></div>
                  
                  {/* Barra de carga animada */}
                  <div className="absolute inset-1 rounded-lg overflow-hidden bg-zinc-900">
                    <div className="h-full bg-gradient-to-r from-gold-500 via-green-400 to-gold-500 animate-pulse"
                      style={{ 
                        width: '70%',
                        animation: 'loadingBar 2s ease-in-out infinite',
                        boxShadow: '0 0 20px rgba(34, 211, 238, 0.5)'
                      }}>
                    </div>
                    {/* Efecto de brillo */}
                    <div className="absolute inset-0 bg-gradient-to-b from-white/10 to-transparent"></div>
                  </div>
                  
                  {/* Porcentaje */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-white font-bold text-lg drop-shadow-lg animate-pulse">
                      Cargando...
                    </span>
                  </div>
                </div>
                
                {/* Rayos decorativos */}
                <div className="absolute -left-2 top-1/2 -translate-y-1/2 text-yellow-400 animate-bounce">
                  ⚡
                </div>
                <div className="absolute -right-6 top-1/2 -translate-y-1/2 text-yellow-400 animate-bounce delay-300">
                  ⚡
                </div>
              </div>
              
              {/* Texto de estado */}
              <p className="text-gold-400 font-bold text-lg mb-2">Terminal de Trading</p>
              <p className="text-zinc-500 text-sm">Verificando acceso...</p>
              
              {/* Puntos animados */}
              <div className="flex justify-center gap-1 mt-4">
                {[0, 1, 2].map((i) => (
                  <div 
                    key={i}
                    className="w-2 h-2 rounded-full bg-gold-500"
                    style={{ 
                      animation: `bounce 1s ease-in-out ${i * 0.2}s infinite`
                    }}
                  ></div>
                ))}
              </div>
              
              {/* Botón de Recargar */}
              <button
                onClick={() => window.location.reload()}
                className="mt-8 px-6 py-2.5 bg-gradient-to-r from-gold-600 to-gold-500 hover:from-gold-500 hover:to-gold-400 text-white font-semibold rounded-xl shadow-lg shadow-gold-500/25 hover:shadow-gold-500/40 transition-all duration-300 flex items-center gap-2 mx-auto group"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 group-hover:rotate-180 transition-transform duration-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Recargar página
              </button>
              <p className="text-zinc-600 text-xs mt-2">Si tarda demasiado, haz clic aquí</p>
            </div>
            
            {/* Estilos de animación */}
            <style>{`
              @keyframes loadingBar {
                0%, 100% { width: 30%; }
                50% { width: 90%; }
              }
              @keyframes bounce {
                0%, 100% { transform: translateY(0); opacity: 1; }
                50% { transform: translateY(-8px); opacity: 0.5; }
              }
            `}</style>
          </div>
        </div>
      </ThemeProvider>
    );
  }

  // Mostrar login si no está autenticado (ya está manejado arriba, pero dejamos como fallback)
  if (!isAuthenticated && currentPage !== 'payment-success') {
    return (
      <ThemeProvider>
        <AuthPage 
          onSuccess={handleLoginSuccess}
          onBack={() => setCurrentPage('landing')}
        />
      </ThemeProvider>
    );
  }

  const priceChange = stockHistory.length >= 2 
    ? stockHistory[stockHistory.length - 1]?.close - stockHistory[stockHistory.length - 2]?.close
    : 0;
  const priceChangePercent = stockHistory.length >= 2 && stockHistory[stockHistory.length - 2]?.close
    ? (priceChange / stockHistory[stockHistory.length - 2].close) * 100
    : 0;

  return (
    <ThemeProvider>
    <LoadingProvider>
    <div className="App">
      {/* Toast removido por solicitud del usuario */}
      
      {/* Panel de Atajos de Teclado */}
      <KeyboardShortcutsPanel 
        isOpen={showShortcutsPanel} 
        onClose={() => setShowShortcutsPanel(false)} 
      />
      
      {/* Sidebar 3D */}
      <Sidebar3D 
        onSectionChange={(sectionId, options) => {
          // Caso especial para notificaciones - abre modal nativo
          if (sectionId === 'notifications') {
            setShowNotificationModal(true);
            return;
          }
          // Abrir ventana flotante con opciones adicionales si las hay
          openWindow(sectionId, options);
        }}
        newsCount={newsCount}
        alertsCount={alertsCount}
        onCollapseChange={setSidebarCollapsed}
        onShowKeyboardShortcuts={() => setShowShortcutsPanel(true)}
        onOpenAlertsPanel={() => setShowAlertsPanel(true)}
        initialCollapsed={sidebarCollapsed}
        forceCollapsed={sidebarCollapsed}
        currentUser={currentUser}
        onShowPricing={() => setCurrentPage('pricing')}
      />
      
      {/* Menú hamburguesa para móvil */}
      <MobileMenu
        onSectionChange={(sectionId, options) => {
          if (sectionId === 'notifications') {
            setShowNotificationModal(true);
            return;
          }
          openWindow(sectionId, options);
        }}
        newsCount={newsCount}
        alertsCount={alertsCount}
        onOpenAlertsPanel={() => setShowAlertsPanel(true)}
      />
      
      {/* Partículas flotantes de fondo */}
      <FloatingParticles count={25} />
      
      {/* Ticker Horizontal en la parte superior */}
      <StockTickerHorizontal />
      
      {/* Main content - con margen para el sidebar (solo en md+) */}
      <div 
        className={`min-h-screen p-4 lg:p-6 transition-all duration-500 ease-out relative z-10 
          ml-0 md:ml-16 ${!sidebarCollapsed ? 'md:ml-56' : ''}`}
      >
        {/* Header LIMPIO - Logo y Reloj GRANDES */}
        <div className="mb-6 lg:mb-8 border-b border-border pb-4 glass-card rounded-xl p-3 sm:p-6 relative overflow-hidden" data-testid="app-header">
          {/* Acento dorado de marca (sutil, no afecta a los colores de datos) */}
          <div className="absolute top-0 left-0 right-0 h-[3px]" style={{ background: 'linear-gradient(90deg,#A67C00,#E8C55C,#FBE9A0,#E8C55C,#A67C00)' }} />
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            {/* Logo */}
            <div className="flex items-center">
              <img 
                src="/logo-kapital-2.png" 
                alt="KAPITAL" 
                className="h-20 sm:h-32 lg:h-40 object-contain"
                style={{ mixBlendMode: 'lighten' }}
                data-testid="app-title"
              />
            </div>
            
            {/* Reloj GRANDE + Configuración */}
            <div className="flex items-center gap-2 sm:gap-6 flex-wrap justify-center sm:justify-end">
              {/* Aviso discreto: TR cierra a las 23:00 (solo última hora) */}
              <TRCloseCountdown />
              {/* Indicador de actualización - Reloj GRANDE */}
              <UpdateIndicator 
                loading={loading}
                loadingProgress={loadingProgress}
                lastUpdate={lastUpdate}
                enlarged={true}
              />
              
              {/* Botón Ver Planes - Solo si no es ultimate */}
              {currentUser?.plan !== 'ultimate' && (
                <button
                  onClick={() => setCurrentPage('pricing')}
                  className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-all bg-gradient-to-r from-gold-500 to-orange-500 hover:from-gold-400 hover:to-orange-400 text-white shadow-lg"
                  title="Ver planes y mejorar"
                  data-testid="pricing-button"
                >
                  <span className="hidden sm:inline">⭐ Mejorar Plan</span>
                  <span className="sm:hidden">⭐ Plan</span>
                </button>
              )}
              
              {/* Configuración */}
              <Settings 
                onLogout={handleLogout}
                onShowAlerts={() => setShowAlertsPanel(true)}
                onManualRefresh={handleManualRefresh}
                onSoftRefresh={handleSoftRefresh}
                onSaveCloud={async () => {
                  if (savingToCloud) return;
                  setSavingToCloud(true);
                  setSaveSuccess(false);
                  try {
                    let investments = {};
                    let selectedMarkets = [];
                    try {
                      const invStr = localStorage.getItem('myInvestments');
                      if (invStr) investments = JSON.parse(invStr);
                    } catch (e) { console.warn('Error parsing investments:', e); }
                    try {
                      const marketsStr = localStorage.getItem('popularMarketsSelection');
                      if (marketsStr) selectedMarkets = JSON.parse(marketsStr);
                    } catch (e) { console.warn('Error parsing markets:', e); }
                    
                    // El servidor es la fuente de verdad de QUÉ posiciones existen (ticket 266909):
                    // no re-subir activos que sólo quedan en una copia local antigua (vendidos).
                    try {
                      const srv = await axios.get(`${API}/user/investments?_t=${Date.now()}`, { withCredentials: true });
                      const srvInv = srv.data?.investments || {};
                      if (Object.keys(srvInv).length > 0) {
                        Object.keys(investments).forEach(t => { if (!srvInv[t]) delete investments[t]; });
                      }
                    } catch (e) { console.warn('No se pudo leer el servidor antes de guardar:', e.message); }
                    await axios.put(`${API}/user/investments`, { investments }, { withCredentials: true });
                    await axios.put(`${API}/user/markets`, { markets: selectedMarkets }, { withCredentials: true });
                    
                    setSaveSuccess(true);
                    setTimeout(() => setSaveSuccess(false), 3000);
                  } catch (error) {
                    console.error('Error guardando:', error);
                  }
                  setSavingToCloud(false);
                }}
                onOpenBackup={() => openWindow('backup')}
                loading={loading}
                savingToCloud={savingToCloud}
                saveSuccess={saveSuccess}
                alertsCount={alertsCount}
                currentUser={currentUser}
                onOpenAdmin={() => setCurrentPage('admin')}
                isAdmin={currentUser && ['admin@tradingterm.com', 'alfonsotorrecillamartin@gmail.com', 'atorremartin73@gmail.com', 'kapital.analisistecnico@gmail.com'].includes(currentUser.email)}
              />
            </div>
          </div>
        </div>
        
        {/* Indicador de Progreso por Fases - DESACTIVADO (el usuario prefiere solo la barra inline en MERCADOS POPULARES) */}
        {/* <PhaseLoadingProgress 
          currentPhase={currentPhase}
          completedPhases={completedPhases}
          progress={queueProgress}
          ticker={ticker}
          isVisible={isQueueLoading || loading}
          getSectionStatus={getSectionStatus}
          LOADING_PHASES={LOADING_PHASES}
          SECTION_CONFIG={SECTION_CONFIG}
          onReload={handleManualRefresh}
          fastLoadMode={fastLoadMode}
          onToggleFastLoad={toggleFastLoadMode}
        /> */}

        {/* Main Grid */}
        <div className="space-y-4 lg:space-y-6">
          {/* LazyDashboardZones - Componente lazy loaded con skeleton fallback */}
          <LazyDashboardZones 
            isVisible={isVisible}
            ticker={ticker}
            stockInfo={stockInfo}
            stockHistory={stockHistory}
            loading={loading}
            priceChange={priceChange}
            priceChangePercent={priceChangePercent}
            chartPeriod={chartPeriod}
            setChartPeriod={setChartPeriod}
            handlePeriodChange={handlePeriodChange}
            handleTickerSelect={handleTickerSelect}
            portfolioTickers={portfolioTickers}
            CHART_PERIODS={CHART_PERIODS}
            // Props para carga por fases
            isSectionEnabled={isSectionEnabled}
            startSectionLoading={startSectionLoading}
            completeSectionLoading={completeSectionLoading}
            onEnrichedMarketsChange={setEnrichedMarketsForBigScreen}
            onRefreshChart={() => fetchStockData(ticker, chartPeriod.period, chartPeriod.interval, true, chartPeriod.aggregate || 1)}
            // User plan para restricciones
            currentUser={currentUser}
            onShowPricing={() => setCurrentPage('pricing')}
          openWindow={openWindow}  // job 6cdb8552: chart-header shortcuts (Paper Trading / Patrones / pop-out)
            restoreLastSession={restoreLastSession}
            getSavedSessionCount={getSavedSessionCount}
            />
        </div>
      </div>
      
      {/* Sistema de Ventanas Flotantes */}
      <WindowManager
        openWindows={openWindows}
        minimizedWindows={minimizedWindows}
        focusedWindow={focusedWindow}
        onClose={closeWindow}
        onFocus={focusWindow}
        onMinimize={minimizeWindow}
        onStateChange={updateWindowState}
        openWindow={openWindow}
        ticker={ticker}
        stockHistory={stockHistory}
        stockInfo={stockInfo}
        onTickerSelect={handleTickerSelect}
        portfolioTickers={portfolioTickers}
        enrichedMarkets={enrichedMarketsForBigScreen}
      />
      
      {/* Rótulos persistentes de cruce de niveles marcados */}
      <LevelCrossWatcher onTickerSelect={handleTickerSelect} />
      
      {/* Taskbar - Barra de tareas para ventanas minimizadas */}
      <Taskbar
        minimizedWindows={getMinimizedWindowsInfo()}
        openWindows={openWindows}
        onRestore={restoreWindow}
        onClose={closeWindow}
        onMinimizeAll={minimizeAllWindows}
        onRestoreAll={restoreAllWindows}
        onCloseAll={closeAllWindows}
        openWindowsCount={openWindows.length}
        alertsMinimized={alertsPanelMinimized && showAlertsPanel}
        onRestoreAlerts={() => setAlertsPanelMinimized(false)}
        onCloseAlerts={() => {
          setShowAlertsPanel(false);
          setAlertsPanelMinimized(false);
        }}
      />
      
      {/* Vista Móvil Compacta retirada a petición del usuario: se deja libre la
          zona inferior en móvil (solo queda la píldora "App lista"). */}
      
      {/* Modal de Notificaciones */}
      <NotificationManager 
        isOpen={showNotificationModal}
        onClose={() => setShowNotificationModal(false)}
      />
      
      {/* Modal de Historial de Alertas */}
      <AlertHistory
        isOpen={showAlertHistory}
        onClose={() => setShowAlertHistory(false)}
      />

      {/* Modal Mis Compartidos (feb 2026) */}
      <MySharesPanel
        open={showMyShares}
        onClose={() => setShowMyShares(false)}
      />
      
      {/* Panel de Alertas de Precio */}
      <AlertsPanel
        isOpen={showAlertsPanel && !alertsPanelMinimized}
        onClose={() => {
          setShowAlertsPanel(false);
          setAlertsPanelMinimized(false);
        }}
        onMinimize={() => {
          setAlertsPanelMinimized(true);
        }}
        markets={enrichedMarketsForBigScreen}
      />
      
      {/* Zona de comparación rápida con drag & drop */}
      <QuickCompareZone 
        onOpenFullComparator={(tickers) => {
          // Abrir el comparador completo con los tickers seleccionados
          openWindow('comparator', { initialTickers: tickers });
        }}
      />
      
      {/* Indicador de progreso de carga - muestra cuando la app está lista */}
      <AppReadyIndicator />
      
      {/* Banner de instalación PWA */}
      <InstallPWAButton variant="banner" />
    </div>
    </LoadingProvider>
    </ThemeProvider>
  );
}

// Componente principal que envuelve todo con los providers necesarios
function App() {
  return (
    <ExportHistoryProvider>
      <NewsProvider>
        <AlertsProvider>
          <AppContent />
          <Toaster
            position="top-center"
            closeButton
            theme="dark"
            toastOptions={{
              className: 'kap-toast',
              style: {
                background: 'rgba(9, 9, 11, 0.95)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                color: '#e4e4e7',
                backdropFilter: 'blur(14px)',
                WebkitBackdropFilter: 'blur(14px)',
                borderRadius: '14px',
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.55)',
                fontWeight: 600,
                letterSpacing: '-0.01em',
              },
            }}
          />
        </AlertsProvider>
      </NewsProvider>
    </ExportHistoryProvider>
  );
}


export default App;
